import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  Link,
  styled,
  Table,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';

export const PageContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
`;

export const TopHeader = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 28px;
  line-height: 36px;
  letter-spacing: 0%;
  color: #000000;
  width: 330px;
  height: 36px;
  vertical-align: middle;
  margin-bottom: 28px;
`;

export const SectionBox = styled('div')`
  margin-top: 4px;
`;

export const ScrollSectionBox = styled('div')`
  overflow-y: auto;
  flex: 1;
`;

export const StyledTableCell = styled(TableCell)`
  font-weight: 700;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  text-align: center;
  vertical-align: middle;
  padding: 14px 12px;
`;

export const EventsHeaderCell = styled(TableCell)`
  text-align: left;
  padding: 14px 0px 14px 16px;
  width: 260px;
  box-sizing: border-box;
  font-weight: 700;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;
`;

export const SwapHeaderCell = styled(StyledTableCell)`
  text-align: center;
  width: 87px;
  padding: 14px 4px;
  box-sizing: border-box;
`;

export const ThemeHeaderCell = styled(StyledTableCell)`
  text-align: left;
  width: 140px;
  padding: 14px 16px;
  box-sizing: border-box;
`;

export const DaypartHeaderCell = styled(StyledTableCell)`
  text-align: center;
  width: 12%;
  padding: 14px 16px;
  box-sizing: border-box;
`;

export const InnerRow = styled(TableRow)`
  border-bottom: 1px solid #f0f0f0;
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
  height: 54px;
  vertical-align: middle;
`;

export const InnerTableCell = styled(TableCell)`
  padding: 8px 12px;
  vertical-align: middle;
  opacity: 1;
  border-bottom: 1px solid #e0e0e0;
`;

// Wrapper for TableContainer to centralize container styles
export const TableWrapper = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

export const CircularUncheckedIcon = styled('span')<{
  $isViewOnly?: boolean;
  theme?: any;
}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $isViewOnly }: { $isViewOnly?: boolean }) =>
      $isViewOnly ? '#bdbdbd' : 'rgba(0, 0, 0, 0.54)'};
  background-color: ${({ $isViewOnly }: { $isViewOnly?: boolean }) =>
    $isViewOnly ? '#f5f5f5' : 'transparent'};
  display: inline-block;
  box-sizing: border-box;
`;

export const CircularCheckedIcon = styled('span')<{
  $isViewOnly?: boolean;
  theme?: any;
}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid
    ${({ $isViewOnly, theme }: { $isViewOnly?: boolean; theme?: any }) =>
      $isViewOnly ? '#bdbdbd' : theme.palette.primary.main};
  background-color: ${({ $isViewOnly }: { $isViewOnly?: boolean }) =>
    $isViewOnly ? '#f5f5f5' : 'white'};
  display: inline-block;
  box-sizing: border-box;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: ${({
      $isViewOnly,
      theme,
    }: {
      $isViewOnly?: boolean;
      theme?: any;
    }) => ($isViewOnly ? '#bdbdbd' : theme.palette.primary.main)};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

// Styled Checkbox with round design for dayparts
export const StyledCheckbox = styled(Checkbox)<{ $isViewOnly?: boolean }>`
  & .MuiIconButton-root {
    border-radius: 50% !important;
    padding: 4px;
  }
  ${({ $isViewOnly }: { $isViewOnly?: boolean }) =>
    $isViewOnly &&
    `
      &.Mui-disabled,
      &.Mui-disabled .MuiIconButton-root,
      &.MuiCheckbox-root.Mui-disabled,
      &.MuiCheckbox-root.Mui-disabled:hover,
      &.MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root {
        color: #bdbdbd !important;
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
        pointer-events: none;
        opacity: 0.7;
      }
      &.MuiCheckbox-root.Mui-disabled:hover {
        color: #bdbdbd !important;
        background-color: #f5f5f5 !important;
        cursor: not-allowed !important;
        opacity: 0.7;
      }
    `}
`;

// Inner cell width variants to keep JSX clean
export const InnerCellEvents = styled(InnerTableCell)`
  width: 260px;
  padding: 6px 16px 6px 100px !important;
  opacity: 1;
  position: relative;
`;

export const InnerCellSwap = styled(InnerTableCell)`
  width: 87px;
  height: 52px;
  text-align: center;
  padding: 16px !important;
  opacity: 1;
`;

export const InnerCellTheme = styled(InnerTableCell)`
  width: 140px;
  text-align: left;
  padding: 8px 18px !important;
  opacity: 1;
`;

export const InnerCellDaypart = styled(InnerTableCell)`
  width: 12%;
  text-align: center;
  box-sizing: border-box;
  padding: 8px 12px !important;
  opacity: 1;
`;

export const InnerCellAction = styled(InnerTableCell)`
  width: 48px;
  text-align: center;
  padding: 8px 12px !important;
  opacity: 1;
`;

// Box wrappers used inside inner cells so padding is controlled from styled file
// Cell padding handles left alignment (100px to match header text start position)
export const InnerFirstBox = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  height: 54px;
  padding: 0;
  opacity: 1;
  box-sizing: border-box;
  min-width: 0;
  overflow: hidden;
`;

export const ActionHeaderCell = styled(StyledTableCell)`
  width: 48px;
  background-color: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  box-sizing: border-box;
`;

// Styled link used for component names in inner rows
export const ComponentLink = styled(Link)`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 143%;
  letter-spacing: 0.17px;
  color: #025ac5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  max-width: 100%;
  min-width: 0;
  flex-shrink: 1;
  text-decoration: none !important;
  vertical-align: middle;

  &:hover {
    color: #025ac5;
  }
`;

export const NoComponentIcon = styled('span')`
  display: inline-flex;
`;

export const ThemeMarker = styled('span')<{ height?: string }>`
  display: inline-block;
  width: 4px;
  height: ${({ height }: { height?: string }) => height ?? '28px'};
  border-radius: 2px;
  margin-right: 0px;
  margin-left: 0px;
  flex-shrink: 0;
`;

export const SwapButton = styled(IconButton)`
  padding: 8px 12px;
  border: 1px solid ${(p: any) => p.theme.palette.divider};
  border-radius: 6px;
  width: 34px;
  height: 34px;
  background-color: ${(p: any) => p.theme.palette.grey[50]};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 1;

  &:hover {
    background-color: rgba(0, 89, 196, 0.04);
    border-color: ${(p: any) => p.theme.palette.text.primary};
  }

  svg {
    color: var(--action-active, #0000008f);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// selection section
export const SelectionBox = styled(Box)`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 0;

  & > hr {
    margin-top: 0;
    margin-bottom: 8px;
    margin-left: 0;
    margin-right: 0;
  }
`;

export const DaypartLabel = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 20px;
  line-height: 26px;
  letter-spacing: 0%;
  color: rgba(0, 0, 0, 0.87);
  width: 100%;
  vertical-align: middle;
  margin-bottom: 8px;
`;

export const DaypartValue = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 20px;
  line-height: 26px;
  letter-spacing: 0%;
  color: rgba(0, 0, 0, 0.87);
  width: 73px;
  text-align: left;
  vertical-align: middle;
  padding: 8px 0 0;
  text-decoration: none;
  display: inline-block;
  position: relative;
`;

export const DriveThruDirection = styled(Typography)(
  ({ theme }) => `
    color: ${theme.palette.grey[500]};
  `,
);

// Main table
export const MainTable = styled(Table)`
  table-layout: fixed;
  width: 100%;
`;

// Event header row
export const EventHeaderRow = styled(TableRow)<{ $selected?: boolean }>`
  background-color: ${(p: any) => (p.$selected ? '#e3f2fd' : 'transparent')};

  &:hover {
    background-color: ${(p: any) => (p.$selected ? '#e3f2fd' : '#f5f5f5')};
  }
`;

export const EventHeaderCell = styled(TableCell)<{ $selected?: boolean }>`
  text-align: left;
  padding: 14px 16px;
  width: 260px;
  box-sizing: border-box;
  background-color: ${(p: any) => (p.$selected ? '#e3f2fd' : '#f9fafb')};
  border-bottom: 1px solid #e5e7eb;
  vertical-align: middle;

  /* Calculate where text starts: padding-left (16px) + icon (20px) + gap (12px) + arrow (24px) + gap (12px) + marker (4px) + gap (12px) = 100px */
`;

export const EventHeaderCellContent = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 24px;
`;

export const StyledCheckCircleIcon = styled(CheckCircleIcon)`
  color: #1976d2;
`;

export const StyledErrorOutlineRoundedIcon = styled(ErrorOutlineRoundedIcon)`
  color: #fbc02d;
`;

export const ExpandIconButton = styled(IconButton)`
  padding: 0;
`;

export const EventNameTypography = styled(Typography)`
  font-family: 'Roboto', sans-serif;
  font-weight: 700;
  font-style: normal;
  font-size: 14px;
  line-height: 24px;
  letter-spacing: 0.17px;
  padding: 0;
  color: rgba(0, 0, 0, 0.87);
`;

export const EmptyEventTableCell = styled(TableCell)<{ $selected?: boolean }>`
  background-color: ${(p: any) => (p.$selected ? '#e3f2fd' : '#f9fafb')};
  border-bottom: 1px solid #e5e7eb;
`;

export const EmptyEventTableCellSwap = styled(EmptyEventTableCell)`
  width: 87px;
`;

export const EmptyEventTableCellTheme = styled(EmptyEventTableCell)`
  width: 140px;
`;

export const EmptyEventTableCellDaypart = styled(EmptyEventTableCell)`
  width: 12%;
`;

export const EmptyEventTableCellAction = styled(EmptyEventTableCell)`
  width: 48px;
  text-align: center;
  vertical-align: middle;
`;

// Component rows
export const ComponentRow = styled(TableRow)`
  height: 54px;
  vertical-align: middle;
  &:hover {
    background-color: rgba(0, 89, 196, 0.04);
  }
`;

export const EmptyComponentTableCell = styled(TableCell)``;

export const EmptyComponentTableCellTheme = styled(EmptyComponentTableCell)`
  width: 140px;
`;

export const EmptyComponentTableCellDaypart = styled(EmptyComponentTableCell)`
  width: 12%;
`;

export const EmptyComponentTableCellAction = styled(EmptyComponentTableCell)`
  width: 48px;
`;

// Footer
export const FooterDivider = styled(Divider)`
  margin-top: 32px;
  margin-bottom: 16px;
`;

export const FooterActionsBox = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding: 16px 0;
`;

export const CancelButton = styled(Button)`
  color: ${(p: any) => p.theme.palette.text.secondary};
  text-transform: none;
  font-size: 0.875rem;
  font-weight: 500;
`;

export const SaveButton = styled(Button)`
  text-transform: none;
  font-size: 0.875rem;
  font-weight: 500;
  padding: 0 24px;
`;
